import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook for admin data management
 * Handles users, stats, and general admin data fetching
 * 
 * FIXED: Plans tab infinite loading issue (2025-07-29)
 * 
 * Issue: Plans tab was stuck in infinite loading state despite API working
 * Root Cause: Plans tab depends on users data for assign plan dropdown,
 *            but users were only fetched when activeTab === 'users'
 * Solution: Added users fetch for 'subscriptions' tab as well
 * 
 * Data Dependencies:
 * - 'overview' tab → needs stats data
 * - 'users' tab → needs users data  
 * - 'subscriptions' tab → needs users data (for assign plan dropdown)
 */
export const useAdminData = (activeTab) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Fetch admin statistics
  const fetchStats = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        setError('Authentication required');
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setStats(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError('Error fetching statistics');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        setError('Authentication required');
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setUsers(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error fetching users');
    } finally {
      setLoading(false);
    }
  };
  
  // Effect to fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchStats();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'subscriptions') {
      // Plans tab needs users data for assign plan dropdown
      // FIX: Infinite loading issue - Plans tab was stuck loading because users never fetched
      fetchUsers();
    } else if (activeTab === 'reports') {
      // Reports tab needs users data for user summary dropdown
      fetchUsers();
    }
  }, [activeTab]);
  
  return {
    users,
    setUsers,
    stats,
    loading,
    error,
    fetchStats,
    fetchUsers
  };
};